import * as React from 'react';
import * as jsDiff from 'diff';
import _ from 'underscore';
import parse from 'html-react-parser'

import { ConceptResults } from '../questions/conceptResults';
import { extractConceptResultsFromResponse, getStatusForResponse } from '../../libs/responseTools';
import { TextEditor } from '../shared/textEditor';

interface ResponseProps {
  allExpanded: boolean;
  ascending?: boolean;
  concepts: any;
  Constants: any;
  deleteResponse: Function;
  dispatch: Function;
  expand: Function;
  expanded: boolean;
  fillInTheBlank?: boolean;
  getGradedResponsesWithCallback: Function;
  getResponse: Function;
  massEdit: any;
  massEditActions: any;
  mode: string;
  passedResponse: any;
  question: any;
  questionActions: any;
  questionID: string;
  readOnly: boolean;
  rematchOne?: Function;
  responses: Array<any>;
  state: any;
  states: any;
  submitResponseEdit: Function;
  updateParentResponse?: Function;
}

export const Response: React.FC<ResponseProps> = ({
  allExpanded,
  ascending,
  concepts,
  Constants,
  deleteResponse,
  dispatch,
  expand,
  expanded,
  fillInTheBlank,
  getGradedResponsesWithCallback,
  getResponse,
  massEdit: massEditResponses,
  massEditActions,
  mode,
  passedResponse,
  question,
  questionActions,
  questionID,
  readOnly,
  rematchOne,
  responses,
  state,
  states,
  submitResponseEdit,
  updateParentResponse
}) => {
  const [response, setResponse] = React.useState(passedResponse);
  const [feedback, setFeedback] = React.useState(passedResponse.feedback || '');
  const [selectedBoilerplate, setSelectedBoilerplate] = React.useState('');
  const [parent, setParent] = React.useState(null);
  const [conceptResults, setConceptResults] = React.useState(
    extractConceptResultsFromResponse(response)
  );
  const [statusCode, setStatusCode] = React.useState(getStatusForResponse(passedResponse));
  const newResponseOptimal = React.useRef(null);

  React.useEffect(() => {
    setFeedback(response.feedback);
    setStatusCode(getStatusForResponse(response));
    setConceptResults(extractConceptResultsFromResponse(response));
  }, [response]);

  React.useEffect(() => {
    setResponse(passedResponse);
  }, [passedResponse]);

  function handleDeleteResponse() {
    if (window.confirm('Are you sure?')) {
      dispatch(deleteResponse(questionID, response.id));
      dispatch(massEditActions.removeResponseFromMassEditArray(response.id));
    }
  }

  function editResponse() {
    dispatch(questionActions.startResponseEdit(questionID, response.id));
  }

  function cancelResponseEdit() {
    dispatch(questionActions.cancelResponseEdit(questionID, response.id));
  }

  function updateResponse() {
    const submittableConceptResults = _.omit(conceptResults, 'null', 'undefined');
    const newResp = {
      weak: false,
      feedback,
      optimal: newResponseOptimal.current.checked,
      author: null,
      parent_id: null,
      concept_results:
        Object.keys(submittableConceptResults).length ? submittableConceptResults : null
    };
    dispatch(submitResponseEdit(response.id, newResp, questionID, rerenderResponse));
  }

  function unmatchResponse() {
    const { modelConceptUID, conceptID } = question;
    const defaultConceptUID = modelConceptUID || conceptID;
    const newResp = {
      weak: false,
      feedback: null,
      optimal: null,
      author: null,
      parent_id: null,
      concept_results: defaultConceptUID ? { [defaultConceptUID]: false } : null
    };
    dispatch(submitResponseEdit(response.id, newResp, questionID, rerenderResponse));
  }

  function unmatchAndRematch() {
    unmatchResponse()
    rematchResponse()
  }

  function rematchResponse() {
    if (rematchOne) {
      rematchOne(response, mode, question, questionID, rerenderResponse, dispatch);
    }
  }

  function applyDiff(answer = '', response = '') {
    const diff = jsDiff.diffWords(response, answer);
    const spans = diff.map((part, index) => {
      const fontWeight = part.added ? 'bold' : 'normal';
      const fontStyle = part.removed ? 'oblique' : 'normal';
      const divStyle = {
        fontWeight,
        fontStyle
      };
      return (
        <span key={index} style={divStyle}>
          {part.value}
        </span>
      );
    });
    return spans;
  }

  function handleFeedbackChange(e) {
    if (e === 'Select specific boilerplate feedback') {
      setFeedback('');
    } else {
      setFeedback(e);
    }
  }

  function deleteConceptResult(crid) {
    if (confirm('Are you sure?')) {
      let conceptResultsTemp = Object.assign({}, conceptResults || {});
      delete conceptResultsTemp[crid];
      setConceptResults(conceptResultsTemp);
    }
  }

  function addResponseToMassEditArray(responseKey) {
    dispatch(massEditActions.addResponseToMassEditArray(responseKey));
  }

  function removeResponseFromMassEditArray(responseKey) {
    dispatch(massEditActions.removeResponseFromMassEditArray(responseKey));
  }

  function onMassSelectCheckboxToggle() {
    const { selectedResponses } = massEditResponses;
    if (selectedResponses.includes(response.id)) {
      removeResponseFromMassEditArray(response.id);
    } else {
      addResponseToMassEditArray(response.id);
    }
  }

  function toggleCheckboxCorrect(key) {
    if (key === 'null') { return; }

    const updatedResults = { ...conceptResults, [key]: !conceptResults[key] };
    setConceptResults(updatedResults);
  }

  function handleConceptChange(e) {
    const conceptsTemp = { ...conceptResults };
    if (Object.keys(conceptsTemp).length === 0 || !conceptsTemp.hasOwnProperty(e.value)) {
      conceptsTemp[e.value] = response.optimal;
      setConceptResults(conceptsTemp);
    }
  }

  function getParentResponse(parent_id) {
    const callback = (responses) => {
      setParent(_.filter(responses, (resp) => resp.id === parent_id)[0]);
    };
    return getGradedResponsesWithCallback(questionID, callback);
  }

  function rerenderResponse(newResponse?) {
    if (updateParentResponse) {
      if (newResponse) {
        updateParentResponse(newResponse.id, newResponse);
      } else {
        updateParentResponse();
      }
    }
  }


  function renderResponseContent(isEditing, response) {
    let content;
    let parentDetails;
    let childDetails;
    let pathwayDetails;
    let authorDetails;

    if (!expanded) {
      return;
    }

    if (response.parentID || response.parent_id) {
      const responseParent = parent;
      if (!responseParent) {
        getParentResponse(response.parentID || response.parent_id);
        parentDetails = [<p key="loading">Loading...</p>, <br key="br" />];
      } else {
        const diffText = applyDiff(responseParent.text, response.text);
        parentDetails = [
          <span key="parent-text">
            <strong>Parent Text:</strong> {responseParent.text}
          </span>,
          <br key="br1" />,
          <span key="parent-feedback">
            <strong>Parent Feedback:</strong> {responseParent.feedback}
          </span>,
          <br key="br2" />,
          <span key="differences">
            <strong>Differences:</strong> {diffText}
          </span>,
          <br key="br3" />,
          <br key="br4" />
        ];
      }
    }

    const conceptResultsContent = (
      <ConceptResults
        conceptResults={conceptResults}
        concepts={concepts}
        deleteConceptResult={deleteConceptResult}
        fillInTheBlank={fillInTheBlank}
        handleConceptChange={handleConceptChange}
        mode={isEditing ? 'Editing' : 'Viewing'}
        response={response}
        toggleCheckboxCorrect={toggleCheckboxCorrect}
      />
    );

    if (isEditing) {
      content = (
        <div className="content">
          {parentDetails}
          <label className="label">Feedback</label>
          <TextEditor
            boilerplate={selectedBoilerplate}
            handleTextChange={handleFeedbackChange}
            text={feedback || ''}
          />

          <br />

          <div className="box">
            <label className="label">Concept Results</label>
            {conceptResultsContent}
          </div>

          <p className="control">
            <label className="checkbox">
              <input defaultChecked={response.optimal} ref={newResponseOptimal} type="checkbox" />
              Optimal?
            </label>
          </p>
        </div>
      );
    } else {
      content = (
        <div className="content">
          {parentDetails}
          <strong>Feedback:</strong> <br />
          <div dangerouslySetInnerHTML={{ __html: response.feedback }} />
          <br />
          <label className="label">Concept Results</label>
          {conceptResultsContent}
          {authorDetails}
          {childDetails}
          {pathwayDetails}
        </div>
      );
    }

    return <div className="card-content">{content}</div>;
  }

  function renderResponseFooter(isEditing, response) {
    if (!readOnly || !expanded) {
      return;
    }
    let buttons;

    if (question.question_type === 'grammar_fill_in_blanks') {
      if (isEditing) {
        buttons = [
          <a className="card-footer-item" key="cancel" onClick={cancelResponseEdit}>
            Cancel
          </a>,
          <a className="card-footer-item" key="update" onClick={updateResponse}>
            Save
          </a>
        ];
      } else {
        buttons = [
          <a className="card-footer-item" key="edit" onClick={editResponse}>
            Edit
          </a>,
          <a className="card-footer-item" key="unmatch" onClick={unmatchAndRematch}>
            Remove Manual Grading
          </a>
        ]
      }
    } else {
      if (isEditing) {
        buttons = [
          <a className="card-footer-item" key="cancel" onClick={cancelResponseEdit}>
            Cancel
          </a>,
          <a className="card-footer-item" key="unmatch" onClick={unmatchResponse}>
            Unmatch
          </a>,
          <a className="card-footer-item" key="update" onClick={updateResponse}>
            Update
          </a>
        ];
      } else {
        buttons = [
          <a className="card-footer-item" key="edit" onClick={editResponse}>
            Edit
          </a>,
          <a className="card-footer-item" key="delete" onClick={handleDeleteResponse}>
            Delete
          </a>
        ];
      }
      if (statusCode > 1 && rematchOne) {
        buttons = buttons.concat([
          <a className="card-footer-item" key="rematch" onClick={rematchResponse}>
            Rematch
          </a>
        ]);
      }

    }

    return <footer className="card-footer">{buttons}</footer>;
  }

  function renderResponseHeader(response) {
    let bgColor;
    let icon;
    const headerCSSClassNames = [
      'human-optimal-response',
      'human-sub-optimal-response',
      'algorithm-optimal-response',
      'algorithm-sub-optimal-response',
      'not-found-response'
    ];
    bgColor = headerCSSClassNames[statusCode];
    if (response.weak) {
      icon = '⚠️';
    }
    const authorStyle = { marginLeft: '10px' };
    const showTag = response.author && (statusCode === 2 || statusCode === 3);
    const author = showTag ? (
      <span className="tag is-dark" style={authorStyle}>
        {response.author}
      </span>
    ) : undefined;
    const checked = massEditResponses.selectedResponses.includes(response.id) ? 'checked' : '';
    return (
      <div className={bgColor} style={{ display: 'flex', alignItems: 'center' }}>
        <input
          aria-label="Select response"
          checked={checked}
          onChange={onMassSelectCheckboxToggle}
          style={{ marginLeft: '15px' }}
          type="checkbox"
        />
        <header
          className={`card-content ${headerClasses()}`}
          onClick={() => expand(response.key)}
          style={{ flexGrow: '1' }}
        >
          <div className="content">
            <div className="media">
              <div className="media-content">
                <p>
                  <pre>{parse(response.displayText || response.text || 'BLANK RESPONSE TEXT')}</pre>{author}
                </p>
              </div>
              <div className="media-right" style={{ textAlign: 'right' }}>
                <figure className="image is-32x32">
                  <span>
                    {icon} {response.count ? response.count : 0}
                  </span>
                </figure>
              </div>
            </div>
          </div>
        </header>
      </div>
    );
  }

  function cardClasses() {
    if (expanded) {
      return 'has-bottom-margin has-top-margin';
    }
  }

  function headerClasses() {
    if (!expanded) {
      return 'unexpanded';
    }
    return 'expanded';
  }

  const isEditing = state === `${Constants.START_RESPONSE_EDIT}_${response.id}`;

  return (
    <div className={`card is-fullwidth ${cardClasses()}`}>
      {renderResponseHeader(response)}
      {renderResponseContent(isEditing, response)}
      {renderResponseFooter(isEditing, response)}
    </div>
  );
};
